import {
  type GetUserPropertyDetailsBuildingAttributesWebDto,
  RentalManagerLaundryType,
  type UserPropertyBuildingAttributesAirConditioningTypesWebDto,
  type UserPropertyBuildingAttributesApplianceTypesWebDto,
  type UserPropertyBuildingAttributesCommunicationTypesWebDto,
  type UserPropertyBuildingAttributesCommunityRoomTypesWebDto,
  type UserPropertyBuildingAttributesFloorCoveringTypesWebDto,
  type UserPropertyBuildingAttributesHeatingSourceTypesWebDto,
  type UserPropertyBuildingAttributesLeaseLengthsWebDto,
  type UserPropertyBuildingAttributesOutdoorCommonAreaTypesWebDto,
  type UserPropertyBuildingAttributesParkingTypesWebDto,
  type UserPropertyBuildingAttributesSecurityTypesWebDto,
  type UserPropertyBuildingAttributesSportCourtTypesWebDto,
  type UserPropertyBuildingAttributesUtilitiesIncludedWebDto,
  type UserPropertyBuildingAttributesViewTypesWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type CheckBoxOptionProps = { label: string; apiKey: string; category: string | null };
export type RadioOptionProps = { label: string; key: RentalManagerLaundryType };
export type BuidlingFeatureConstantsProps = {
  CHECKBOX_OPTIONS: Record<string, CheckBoxOptionProps | Array<string>>;
  RADIO_OPTIONS?: Record<string, RadioOptionProps>;
};

export type FieldMetadata = {
  label: string;
  apiKey: string;
  feedFieldName?: string;
  category: string | null;
};

export type CategoryKeys<T extends string = string> = Record<string, Array<T>>;

const KeyFeaturesCategoryKeys = {
  airConditioningTypes: ['hasCentral', 'hasWall', 'hasWindow'] as Array<
    keyof UserPropertyBuildingAttributesAirConditioningTypesWebDto
  >,
  floorCoveringTypes: [
    'hasCarpet',
    'hasConcrete',
    'hasHardwood',
    'hasLaminate',
    'hasLinoleum',
    'hasSlate',
    'hasSoftwood',
    'hasTile',
    'hasVinyl',
  ] as Array<keyof UserPropertyBuildingAttributesFloorCoveringTypesWebDto>,
  heatingSourceTypes: ['hasElectric', 'hasGas', 'hasHeatPump', 'hasOil', 'hasPropaneButane', 'hasSolar'] as Array<
    keyof UserPropertyBuildingAttributesHeatingSourceTypesWebDto
  >,
} as const;

const KeyFeaturesFieldMetadata = {
  hasCentral: {
    label: 'Central',
    apiKey: 'hasCentralAirConditioning',
    category: 'airConditioningTypes',
  },
  hasWall: {
    label: 'Wall',
    apiKey: 'hasWallAirConditioning',
    category: 'airConditioningTypes',
  },
  hasWindow: {
    label: 'Window',
    apiKey: 'hasWindowAirConditioning',
    category: 'airConditioningTypes',
  },
  hasHardwood: {
    label: 'Hardwood',
    apiKey: 'hasHardwoodFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasTile: {
    label: 'Tile',
    apiKey: 'hasTileFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasCarpet: {
    label: 'Carpet',
    apiKey: 'hasCarpetFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasLaminate: {
    label: 'Laminate',
    apiKey: 'hasLaminateFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasLinoleum: {
    label: 'Linoleum',
    apiKey: 'hasLinoleumFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasConcrete: {
    label: 'Concrete',
    apiKey: 'hasConcreteFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasVinyl: {
    label: 'Vinyl',
    apiKey: 'hasVinylFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasSlate: {
    label: 'Slate',
    apiKey: 'hasSlateFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasSoftwood: {
    label: 'Softwood',
    apiKey: 'hasSoftwoodFloorCovering',
    category: 'floorCoveringTypes',
  },
  hasHeatPump: {
    label: 'Heat pump',
    apiKey: 'hasHeatPumpHeatingSource',
    category: 'heatingSourceTypes',
  },
  hasElectric: {
    label: 'Electric',
    apiKey: 'hasElectricHeatingSource',
    category: 'heatingSourceTypes',
  },
  hasGas: {
    label: 'Gas',
    apiKey: 'hasGasHeatingSource',
    category: 'heatingSourceTypes',
  },
  hasOil: {
    label: 'Oil',
    apiKey: 'hasOilHeatingSource',
    category: 'heatingSourceTypes',
  },
  hasPropaneButane: {
    label: 'Propane / Butane',
    apiKey: 'hasPropaneButaneHeatingSource',
    category: 'heatingSourceTypes',
  },
  hasSolar: {
    label: 'Solar',
    apiKey: 'hasSolarHeatingSource',
    category: 'heatingSourceTypes',
  },
} as const satisfies Record<string, FieldMetadata>;

const KeyFeaturesConstants: BuidlingFeatureConstantsProps = {
  RADIO_OPTIONS: {
    None: {
      label: 'None',
      key: RentalManagerLaundryType.NONE,
    },
    'In Unit': {
      label: 'Washer-dryer included',
      key: RentalManagerLaundryType.IN_UNIT,
    },
    Hookups: {
      label: 'Washer-dryer hookups',
      key: RentalManagerLaundryType.HOOKUPS,
    },
    Shared: {
      label: 'Shared / in-building',
      key: RentalManagerLaundryType.SHARED,
    },
  },
  CHECKBOX_OPTIONS: {
    ...KeyFeaturesCategoryKeys,
    ...KeyFeaturesFieldMetadata,
  },
};

const UnitFeaturesCategoryKeys = {
  applianceTypes: [
    'hasConvectionOven',
    'hasDishwasher',
    'hasDoubleOven',
    'hasFreezer',
    'hasGarbageDisposal',
    'hasMicrowaveOven',
    'hasOven',
    'hasRange',
    'hasRefrigerator',
    'hasTrashCompactor',
    'hasWasherDryerHookups',
  ] as Array<keyof UserPropertyBuildingAttributesApplianceTypesWebDto>,
  communicationTypes: ['hasBuildingWideWireless', 'hasCableTv', 'hasHighSpeedInternet', 'hasSatelliteTv'] as Array<
    keyof UserPropertyBuildingAttributesCommunicationTypesWebDto
  >,
  otherTypes: ['hasFireplace', 'isFurnished', 'hasPatioBalcony'] as Array<
    keyof GetUserPropertyDetailsBuildingAttributesWebDto
  >,
} as const;

const UnitFeaturesFieldMetadata = {
  hasConvectionOven: {
    label: 'Convection oven',
    apiKey: 'hasConvectionOvenAppliance',
    category: 'applianceTypes',
  },
  hasDishwasher: {
    label: 'Dishwasher',
    apiKey: 'hasDishwasherAppliance',
    // The feed override endpoint resolves the backend field name, which spells it with a capital W.
    feedFieldName: 'hasDishWasherAppliance',
    category: 'applianceTypes',
  },
  hasDoubleOven: {
    label: 'Double oven',
    apiKey: 'hasDoubleOvenAppliance',
    category: 'applianceTypes',
  },
  hasFreezer: {
    label: 'Freezer',
    apiKey: 'hasFreezerAppliance',
    category: 'applianceTypes',
  },
  hasGarbageDisposal: {
    label: 'Garbage disposal',
    apiKey: 'hasGarbageDisposalAppliance',
    category: 'applianceTypes',
  },
  hasWasherDryerHookups: {
    label: 'Washer/dryer hookups',
    apiKey: 'hasWasherDryerHookupsAppliance',
    category: 'applianceTypes',
  },
  hasMicrowaveOven: {
    label: 'Microwave',
    apiKey: 'hasMicrowaveOvenAppliance',
    category: 'applianceTypes',
  },
  hasOven: {
    label: 'Oven',
    apiKey: 'hasOvenAppliance',
    category: 'applianceTypes',
  },
  hasRange: {
    label: 'Range',
    apiKey: 'hasRangeAppliance',
    category: 'applianceTypes',
  },
  hasRefrigerator: {
    label: 'Refrigerator',
    apiKey: 'hasRefrigeratorAppliance',
    category: 'applianceTypes',
  },
  hasTrashCompactor: {
    label: 'Trash compactor',
    apiKey: 'hasTrashCompactorAppliance',
    category: 'applianceTypes',
  },
  hasBuildingWideWireless: {
    label: 'Building-wide wireless',
    apiKey: 'hasBuildingWideWireless',
    category: 'communicationTypes',
  },
  hasSatelliteTv: {
    label: 'Satellite TV',
    apiKey: 'hasSatelliteTv',
    category: 'communicationTypes',
  },
  hasCableTv: {
    label: 'Cable TV',
    apiKey: 'hasCableTv',
    category: 'communicationTypes',
  },
  hasHighSpeedInternet: {
    label: 'High speed internet',
    apiKey: 'hasHighSpeedInternet',
    category: 'communicationTypes',
  },
  hasFireplace: {
    label: 'Fireplace',
    apiKey: 'hasFireplace',
    category: null,
  },
  hasPatioBalcony: {
    label: 'Patio or balcony',
    apiKey: 'hasPatioBalcony',
    category: null,
  },
  isFurnished: {
    label: 'Furnished',
    apiKey: 'isFurnished',
    category: null,
  },
} as const satisfies Record<string, FieldMetadata>;

const UnitFeaturesConstants: BuidlingFeatureConstantsProps = {
  CHECKBOX_OPTIONS: {
    ...UnitFeaturesCategoryKeys,
    ...UnitFeaturesFieldMetadata,
  },
};

const PropertyFeaturesCategoryKeys = {
  viewTypes: ['hasCity', 'hasGolfCourse', 'hasMountain', 'hasPark', 'hasTerritorial', 'hasWater', 'hasWooded'] as Array<
    keyof UserPropertyBuildingAttributesViewTypesWebDto
  >,
  communityRoomTypes: [
    'hasBusinessCenter',
    'hasClubHouse',
    'hasConferenceRoom',
    'hasFitnessCenter',
    'hasGameRoom',
    'hasLibrary',
    'hasLounge',
    'hasPartyRoom',
    'hasPetWashingStation',
    'hasTheater',
  ] as Array<keyof UserPropertyBuildingAttributesCommunityRoomTypesWebDto>,
  securityTypes: ['hasControlledAccess', 'hasDoorman', 'hasGatedEntry', 'hasIntercom', 'hasNightPatrol'] as Array<
    keyof UserPropertyBuildingAttributesSecurityTypesWebDto
  >,
  otherTypes: [
    'hasTwentyFourHourMaintenance',
    'hasAssistedLiving',
    'hasBarbecue',
    'hasBicycleStorage',
    'hasCeilingFan',
    'hasDisabledAccess',
    'hasDryCleaningDropOff',
    'hasElevator',
    'hasGuestSuite',
    'hasHotTub',
    'isIncomeRestricted',
    'hasOnlineMaintenancePortal',
    'hasOnlineRentPayment',
    'hasOnsiteManagement',
    'isOver55',
    'hasPackageService',
    'hasPetPark',
    'hasSauna',
    'smokingAllowed',
    'hasSpanishSpeakingStaff',
    'hasStorageSpace',
    'hasSwimmingPool',
    'hasValetTrash',
  ] as Array<keyof GetUserPropertyDetailsBuildingAttributesWebDto>,
  sportCourtTypes: [
    'hasBasketball',
    'hasBocce',
    'hasMultiPurpose',
    'hasRacquetball',
    'hasShuffleboard',
    'hasSportsField',
    'hasTennis',
    'hasVolleyball',
    'hasOther',
  ] as Array<keyof UserPropertyBuildingAttributesSportCourtTypesWebDto>,
  outdoorCommonAreaTypes: [
    'hasDeck',
    'hasGarden',
    'hasGazebo',
    'hasLawn',
    'hasPatio',
    'hasPicnicArea',
    'hasPlayground',
    'hasRooftopDeck',
    'hasTrail',
    'hasWaterFeature',
  ] as Array<keyof UserPropertyBuildingAttributesOutdoorCommonAreaTypesWebDto>,
} as const;

const PropertyFeaturesFieldMetadata = {
  hasMountain: {
    label: 'Mountain',
    apiKey: 'hasMountainView',
    category: 'viewTypes',
  },
  hasTerritorial: {
    label: 'Territorial',
    apiKey: 'hasTerritorialView',
    category: 'viewTypes',
  },
  hasPark: {
    label: 'Park',
    apiKey: 'hasParkView',
    category: 'viewTypes',
  },
  hasWater: {
    label: 'Water',
    apiKey: 'hasWaterView',
    category: 'viewTypes',
  },
  hasCity: {
    label: 'City',
    apiKey: 'hasCityView',
    category: 'viewTypes',
  },
  hasGolfCourse: {
    label: 'Golf course',
    apiKey: 'hasGolfCourseView',
    category: 'viewTypes',
  },
  hasWooded: {
    label: 'Wooded',
    apiKey: 'hasWoodedView',
    category: 'viewTypes',
  },
  hasFitnessCenter: {
    label: 'Fitness center',
    apiKey: 'hasFitnessCenterCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasClubHouse: {
    label: 'Clubhouse',
    apiKey: 'hasClubHouseCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasBusinessCenter: {
    label: 'Business center',
    apiKey: 'hasBusinessCenterCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasLounge: {
    label: 'Lounge',
    apiKey: 'hasLoungeCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasGameRoom: {
    label: 'Game room',
    apiKey: 'hasGameRoomCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasLibrary: {
    label: 'Library',
    apiKey: 'hasLibraryCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasConferenceRoom: {
    label: 'Conference center',
    apiKey: 'hasConferenceRoomCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasTheater: {
    label: 'Theater',
    apiKey: 'hasTheaterCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasPartyRoom: {
    label: 'Party room',
    apiKey: 'hasPartyRoomCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasPetWashingStation: {
    label: 'Pet washing station',
    apiKey: 'hasPetWashingStationCommunityRoom',
    category: 'communityRoomTypes',
  },
  hasGatedEntry: {
    label: 'Gated entry',
    apiKey: 'hasGatedEntrySecurity',
    category: 'securityTypes',
  },
  hasDoorman: {
    label: 'Doorman',
    apiKey: 'hasDoormanSecurity',
    category: 'securityTypes',
  },
  hasIntercom: {
    label: 'Intercom',
    apiKey: 'hasIntercomSecurity',
    category: 'securityTypes',
  },
  hasControlledAccess: {
    label: 'Controlled access',
    apiKey: 'hasControlledAccessSecurity',
    category: 'securityTypes',
  },
  hasNightPatrol: {
    label: 'Night patrol',
    apiKey: 'hasNightPatrolSecurity',
    category: 'securityTypes',
  },
  hasTwentyFourHourMaintenance: {
    label: '24-hour maintenance',
    apiKey: 'hasTwentyFourHourMaintenance',
    category: null,
  },
  hasAssistedLiving: {
    label: 'Assisted living',
    apiKey: 'hasAssistedLiving',
    category: null,
  },
  hasBarbecue: {
    label: 'Barbecue',
    apiKey: 'hasBarbecue',
    category: null,
  },
  hasBicycleStorage: {
    label: 'Bicycle storage',
    apiKey: 'hasBicycleStorage',
    category: null,
  },
  hasCeilingFan: {
    label: 'Ceiling fan',
    apiKey: 'hasCeilingFan',
    category: null,
  },
  hasDisabledAccess: {
    label: 'Disabled access',
    apiKey: 'hasDisabledAccess',
    category: null,
  },
  hasDryCleaningDropOff: {
    label: 'Dry cleaning drop-off',
    apiKey: 'hasDryCleaningDropOff',
    category: null,
  },
  hasElevator: {
    label: 'Elevator',
    apiKey: 'hasElevator',
    category: null,
  },
  hasGuestSuite: {
    label: 'Guest suite',
    apiKey: 'hasGuestSuite',
    category: null,
  },
  hasHotTub: {
    label: 'Hot tub',
    apiKey: 'hasHotTub',
    category: null,
  },
  isIncomeRestricted: {
    label: 'Income restricted',
    apiKey: 'isIncomeRestricted',
    category: null,
  },
  hasPackageService: {
    label: 'Package service',
    apiKey: 'hasPackageService',
    category: null,
  },
  hasPetPark: {
    label: 'Pet park',
    apiKey: 'hasPetPark',
    category: null,
  },
  hasOnlineMaintenancePortal: {
    label: 'Online maintenance portal',
    apiKey: 'hasOnlineMaintenancePortal',
    category: null,
  },
  hasOnlineRentPayment: {
    label: 'Online rent payment',
    apiKey: 'hasOnlineRentPayment',
    category: null,
  },
  hasOnsiteManagement: {
    label: 'On-site management',
    apiKey: 'hasOnsiteManagement',
    category: null,
  },
  isOver55: {
    label: 'Over 55 community',
    apiKey: 'isOver55',
    category: null,
  },
  hasSauna: {
    label: 'Sauna',
    apiKey: 'hasSauna',
    category: null,
  },
  smokingAllowed: {
    label: 'Smoking allowed',
    apiKey: 'smokingAllowed',
    category: null,
  },
  hasSpanishSpeakingStaff: {
    label: 'Spanish-speaking staff',
    apiKey: 'hasSpanishSpeakingStaff',
    category: null,
  },
  hasStorageSpace: {
    label: 'Storage space',
    apiKey: 'hasStorageSpace',
    category: null,
  },
  hasSwimmingPool: {
    label: 'Swimming pool',
    apiKey: 'hasSwimmingPool',
    category: null,
  },
  hasValetTrash: {
    label: 'Valet trash',
    apiKey: 'hasValetTrash',
    category: null,
  },
  hasTennis: {
    label: 'Tennis',
    apiKey: 'hasTennisSportCourt',
    category: 'sportCourtTypes',
  },
  hasBasketball: {
    label: 'Basketball',
    apiKey: 'hasBasketballSportCourt',
    category: 'sportCourtTypes',
  },
  hasVolleyball: {
    label: 'Volleyball',
    apiKey: 'hasVolleyballSportCourt',
    category: 'sportCourtTypes',
  },
  hasRacquetball: {
    label: 'Racquetball',
    apiKey: 'hasRacquetballSportCourt',
    category: 'sportCourtTypes',
  },
  hasBocce: {
    label: 'Bocce',
    apiKey: 'hasBocceSportCourt',
    category: 'sportCourtTypes',
  },
  hasShuffleboard: {
    label: 'Shuffleboard',
    apiKey: 'hasShuffleboardSportCourt',
    category: 'sportCourtTypes',
  },
  hasMultiPurpose: {
    label: 'Multi-purpose court',
    apiKey: 'hasMultiPurposeSportCourt',
    category: 'sportCourtTypes',
  },
  hasSportsField: {
    label: 'Sports field',
    apiKey: 'hasSportsFieldSportCourt',
    category: 'sportCourtTypes',
  },
  hasOther: {
    label: 'Other',
    apiKey: 'hasOtherSportCourt',
    category: 'sportCourtTypes',
  },
  hasDeck: {
    label: 'Deck',
    apiKey: 'hasDeckOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasGarden: {
    label: 'Garden',
    apiKey: 'hasGardenOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasGazebo: {
    label: 'Gazebo',
    apiKey: 'hasGazeboOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasLawn: {
    label: 'Lawn',
    apiKey: 'hasLawnOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasRooftopDeck: {
    label: 'Rooftop deck',
    apiKey: 'hasRooftopDeckOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasTrail: {
    label: 'Trail',
    apiKey: 'hasTrailOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasPicnicArea: {
    label: 'Picnic area',
    apiKey: 'hasPicnicAreaOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasPlayground: {
    label: 'Playground',
    apiKey: 'hasPlaygroundOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasPatio: {
    label: 'Patio',
    apiKey: 'hasPatioOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
  hasWaterFeature: {
    label: 'Water feature',
    apiKey: 'hasWaterFeatureOutDoorCommonArea',
    category: 'outdoorCommonAreaTypes',
  },
} as const satisfies Record<string, FieldMetadata>;

const PropertyFeaturesConstants: BuidlingFeatureConstantsProps = {
  CHECKBOX_OPTIONS: {
    ...PropertyFeaturesCategoryKeys,
    ...PropertyFeaturesFieldMetadata,
  },
};

const TermFeaturesCategoryKeys = {
  utilitiesIncluded: [
    'hasAirConditioning',
    'hasCableTv',
    'hasElectricity',
    'hasGarbage',
    'hasGas',
    'hasHeating',
    'hasHotWater',
    'hasInternet',
    'hasSewage',
    'hasWater',
  ] as Array<keyof UserPropertyBuildingAttributesUtilitiesIncludedWebDto>,
  leaseLengths: [
    'hasFlexible',
    'hasMonthly',
    'hasSixMonths',
    'hasOneYear',
    'hasLessThanOneYear',
    'hasMoreThanOneYear',
    'hasNone',
  ] as Array<keyof UserPropertyBuildingAttributesLeaseLengthsWebDto>,
  parkingTypes: [
    'hasAttachedGarage',
    'hasCoveredParking',
    'hasDetachedGarage',
    'hasMultiLevelGarage',
    'hasParkingLot',
    'hasUndergroundGarage',
    'hasNoneParking',
  ] as Array<keyof UserPropertyBuildingAttributesParkingTypesWebDto>,
} as const;

const TermFeaturesFieldMetadata = {
  hasAirConditioning: {
    label: 'Air conditioning',
    apiKey: 'hasAirConditioningUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasCableTv: {
    label: 'Cable TV',
    apiKey: 'hasCableTvUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasElectricity: {
    label: 'Electricity',
    apiKey: 'hasElectricityUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasGarbage: {
    label: 'Garbage',
    apiKey: 'hasGarbageUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasGas: {
    label: 'Gas',
    apiKey: 'hasGasUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasHeating: {
    label: 'Heating',
    apiKey: 'hasHeatingUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasHotWater: {
    label: 'Hot water',
    apiKey: 'hasHotWaterUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasInternet: {
    label: 'Internet',
    apiKey: 'hasInternetUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasSewage: {
    label: 'Sewage',
    apiKey: 'hasSewageUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasWater: {
    label: 'Water',
    apiKey: 'hasWaterUtilityIncluded',
    category: 'utilitiesIncluded',
  },
  hasFlexible: {
    label: 'Flexible',
    apiKey: 'hasFlexibleLeaseLengths',
    category: 'leaseLengths',
  },
  hasMonthly: {
    label: 'Monthly',
    apiKey: 'hasMonthlyLeaseLengths',
    category: 'leaseLengths',
  },
  hasSixMonths: {
    label: 'Six months',
    apiKey: 'hasSixMonthsLeaseLengths',
    category: 'leaseLengths',
  },
  hasOneYear: {
    label: 'One year',
    apiKey: 'hasOneYearLeaseLengths',
    category: 'leaseLengths',
  },
  hasLessThanOneYear: {
    label: 'Less than 1 year',
    apiKey: 'hasLessThanOneYearLeaseLengths',
    category: 'leaseLengths',
  },
  hasMoreThanOneYear: {
    label: 'More than 1 year',
    apiKey: 'hasMoreThanOneYearLeaseLengths',
    category: 'leaseLengths',
  },
  hasNone: {
    label: 'None',
    apiKey: 'hasNoneLeaseLengths',
    category: 'leaseLengths',
  },
  hasAttachedGarage: {
    label: 'Attached garage',
    apiKey: 'hasAttachedGarageParking',
    category: 'parkingTypes',
  },
  hasCoveredParking: {
    label: 'Covered parking',
    apiKey: 'hasCoveredParkingParking',
    category: 'parkingTypes',
  },
  hasDetachedGarage: {
    label: 'Detached garage',
    apiKey: 'hasDetachedGarageParking',
    category: 'parkingTypes',
  },
  hasParkingLot: {
    label: 'Parking lot',
    apiKey: 'hasParkingLotParking',
    category: 'parkingTypes',
  },
  hasMultiLevelGarage: {
    label: 'Multi-level garage',
    apiKey: 'hasMultiLevelGarageParking',
    category: 'parkingTypes',
  },
  hasUndergroundGarage: {
    label: 'Underground garage',
    apiKey: 'hasUndergroundGarageParking',
    category: 'parkingTypes',
  },
  hasNoneParking: {
    label: 'None',
    apiKey: 'hasNoneParking',
    category: 'parkingTypes',
  },
} as const satisfies Record<string, FieldMetadata>;

const TermFeaturesConstants: BuidlingFeatureConstantsProps = {
  CHECKBOX_OPTIONS: {
    ...TermFeaturesCategoryKeys,
    ...TermFeaturesFieldMetadata,
  },
};

// Typed array exports for use with countCheckedFeatures
export const HEATING_SOURCE_TYPE_KEYS = KeyFeaturesConstants.CHECKBOX_OPTIONS.heatingSourceTypes as Array<
  keyof UserPropertyBuildingAttributesHeatingSourceTypesWebDto
>;
export const FLOOR_COVERING_TYPE_KEYS = KeyFeaturesConstants.CHECKBOX_OPTIONS.floorCoveringTypes as Array<
  keyof UserPropertyBuildingAttributesFloorCoveringTypesWebDto
>;
export const AIR_CONDITIONING_TYPE_KEYS = KeyFeaturesConstants.CHECKBOX_OPTIONS.airConditioningTypes as Array<
  keyof UserPropertyBuildingAttributesAirConditioningTypesWebDto
>;

export const APPLIANCE_TYPE_KEYS = UnitFeaturesConstants.CHECKBOX_OPTIONS.applianceTypes as Array<
  keyof UserPropertyBuildingAttributesApplianceTypesWebDto
>;
export const COMMUNICATION_TYPE_KEYS = UnitFeaturesConstants.CHECKBOX_OPTIONS.communicationTypes as Array<
  keyof UserPropertyBuildingAttributesCommunicationTypesWebDto
>;
export const UNIT_OTHER_TYPE_KEYS = UnitFeaturesConstants.CHECKBOX_OPTIONS.otherTypes as Array<
  keyof GetUserPropertyDetailsBuildingAttributesWebDto
>;

export const VIEW_TYPE_KEYS = PropertyFeaturesConstants.CHECKBOX_OPTIONS.viewTypes as Array<
  keyof UserPropertyBuildingAttributesViewTypesWebDto
>;
export const COMMUNITY_ROOM_TYPE_KEYS = PropertyFeaturesConstants.CHECKBOX_OPTIONS.communityRoomTypes as Array<
  keyof UserPropertyBuildingAttributesCommunityRoomTypesWebDto
>;
export const SECURITY_TYPE_KEYS = PropertyFeaturesConstants.CHECKBOX_OPTIONS.securityTypes as Array<
  keyof UserPropertyBuildingAttributesSecurityTypesWebDto
>;
export const SPORT_COURT_TYPE_KEYS = PropertyFeaturesConstants.CHECKBOX_OPTIONS.sportCourtTypes as Array<
  keyof UserPropertyBuildingAttributesSportCourtTypesWebDto
>;
export const OUTDOOR_COMMON_AREA_TYPE_KEYS = PropertyFeaturesConstants.CHECKBOX_OPTIONS.outdoorCommonAreaTypes as Array<
  keyof UserPropertyBuildingAttributesOutdoorCommonAreaTypesWebDto
>;
export const PROPERTY_OTHER_TYPE_KEYS = PropertyFeaturesConstants.CHECKBOX_OPTIONS.otherTypes as Array<
  keyof GetUserPropertyDetailsBuildingAttributesWebDto
>;

export const UTILITIES_INCLUDED_KEYS = TermFeaturesConstants.CHECKBOX_OPTIONS.utilitiesIncluded as Array<
  keyof UserPropertyBuildingAttributesUtilitiesIncludedWebDto
>;
export const LEASE_LENGTH_KEYS = TermFeaturesConstants.CHECKBOX_OPTIONS.leaseLengths as Array<
  keyof UserPropertyBuildingAttributesLeaseLengthsWebDto
>;
export const PARKING_TYPE_KEYS = TermFeaturesConstants.CHECKBOX_OPTIONS.parkingTypes as Array<
  keyof UserPropertyBuildingAttributesParkingTypesWebDto
>;

// Typed category-specific exports for air conditioning
export const AIR_CONDITIONING_KEYS = ['hasCentral', 'hasWall', 'hasWindow'] as const;
export type AirConditioningKey = (typeof AIR_CONDITIONING_KEYS)[number];
export const AIR_CONDITIONING_METADATA: Record<AirConditioningKey, FieldMetadata> = {
  hasCentral: KeyFeaturesFieldMetadata.hasCentral,
  hasWall: KeyFeaturesFieldMetadata.hasWall,
  hasWindow: KeyFeaturesFieldMetadata.hasWindow,
};

// Typed category-specific exports for floor covering
export const FLOOR_COVERING_KEYS = [
  'hasCarpet',
  'hasConcrete',
  'hasHardwood',
  'hasLaminate',
  'hasLinoleum',
  'hasSlate',
  'hasSoftwood',
  'hasTile',
  'hasVinyl',
] as const;
export type FloorCoveringKey = (typeof FLOOR_COVERING_KEYS)[number];
export const FLOOR_COVERING_METADATA: Record<FloorCoveringKey, FieldMetadata> = {
  hasCarpet: KeyFeaturesFieldMetadata.hasCarpet,
  hasConcrete: KeyFeaturesFieldMetadata.hasConcrete,
  hasHardwood: KeyFeaturesFieldMetadata.hasHardwood,
  hasLaminate: KeyFeaturesFieldMetadata.hasLaminate,
  hasLinoleum: KeyFeaturesFieldMetadata.hasLinoleum,
  hasSlate: KeyFeaturesFieldMetadata.hasSlate,
  hasSoftwood: KeyFeaturesFieldMetadata.hasSoftwood,
  hasTile: KeyFeaturesFieldMetadata.hasTile,
  hasVinyl: KeyFeaturesFieldMetadata.hasVinyl,
};

// Typed category-specific exports for heating source
export const HEATING_SOURCE_KEYS = [
  'hasElectric',
  'hasGas',
  'hasHeatPump',
  'hasOil',
  'hasPropaneButane',
  'hasSolar',
] as const;
export type HeatingSourceKey = (typeof HEATING_SOURCE_KEYS)[number];
export const HEATING_SOURCE_METADATA: Record<HeatingSourceKey, FieldMetadata> = {
  hasElectric: KeyFeaturesFieldMetadata.hasElectric,
  hasGas: KeyFeaturesFieldMetadata.hasGas,
  hasHeatPump: KeyFeaturesFieldMetadata.hasHeatPump,
  hasOil: KeyFeaturesFieldMetadata.hasOil,
  hasPropaneButane: KeyFeaturesFieldMetadata.hasPropaneButane,
  hasSolar: KeyFeaturesFieldMetadata.hasSolar,
};

// Typed category-specific exports for community rooms
export const COMMUNITY_ROOM_KEYS = [
  'hasBusinessCenter',
  'hasClubHouse',
  'hasConferenceRoom',
  'hasFitnessCenter',
  'hasGameRoom',
  'hasLibrary',
  'hasLounge',
  'hasPartyRoom',
  'hasPetWashingStation',
  'hasTheater',
] as const;
export type CommunityRoomKey = (typeof COMMUNITY_ROOM_KEYS)[number];
export const COMMUNITY_ROOM_METADATA: Record<CommunityRoomKey, FieldMetadata> = {
  hasBusinessCenter: PropertyFeaturesFieldMetadata.hasBusinessCenter,
  hasClubHouse: PropertyFeaturesFieldMetadata.hasClubHouse,
  hasConferenceRoom: PropertyFeaturesFieldMetadata.hasConferenceRoom,
  hasFitnessCenter: PropertyFeaturesFieldMetadata.hasFitnessCenter,
  hasGameRoom: PropertyFeaturesFieldMetadata.hasGameRoom,
  hasLibrary: PropertyFeaturesFieldMetadata.hasLibrary,
  hasLounge: PropertyFeaturesFieldMetadata.hasLounge,
  hasPartyRoom: PropertyFeaturesFieldMetadata.hasPartyRoom,
  hasPetWashingStation: PropertyFeaturesFieldMetadata.hasPetWashingStation,
  hasTheater: PropertyFeaturesFieldMetadata.hasTheater,
};

// Typed exports for appliances
export const APPLIANCE_KEYS = [
  'hasConvectionOven',
  'hasDishwasher',
  'hasDoubleOven',
  'hasFreezer',
  'hasGarbageDisposal',
  'hasMicrowaveOven',
  'hasOven',
  'hasRange',
  'hasRefrigerator',
  'hasTrashCompactor',
  'hasWasherDryerHookups',
] as const;
export type ApplianceKey = (typeof APPLIANCE_KEYS)[number];
export const APPLIANCE_METADATA: Record<ApplianceKey, FieldMetadata> = {
  hasConvectionOven: UnitFeaturesFieldMetadata.hasConvectionOven,
  hasDishwasher: UnitFeaturesFieldMetadata.hasDishwasher,
  hasDoubleOven: UnitFeaturesFieldMetadata.hasDoubleOven,
  hasFreezer: UnitFeaturesFieldMetadata.hasFreezer,
  hasGarbageDisposal: UnitFeaturesFieldMetadata.hasGarbageDisposal,
  hasMicrowaveOven: UnitFeaturesFieldMetadata.hasMicrowaveOven,
  hasOven: UnitFeaturesFieldMetadata.hasOven,
  hasRange: UnitFeaturesFieldMetadata.hasRange,
  hasRefrigerator: UnitFeaturesFieldMetadata.hasRefrigerator,
  hasTrashCompactor: UnitFeaturesFieldMetadata.hasTrashCompactor,
  hasWasherDryerHookups: UnitFeaturesFieldMetadata.hasWasherDryerHookups,
};

// Typed exports for communication
export const COMMUNICATION_KEYS = [
  'hasBuildingWideWireless',
  'hasCableTv',
  'hasHighSpeedInternet',
  'hasSatelliteTv',
] as const;
export type CommunicationKey = (typeof COMMUNICATION_KEYS)[number];
export const COMMUNICATION_METADATA: Record<CommunicationKey, FieldMetadata> = {
  hasBuildingWideWireless: UnitFeaturesFieldMetadata.hasBuildingWideWireless,
  hasCableTv: UnitFeaturesFieldMetadata.hasCableTv,
  hasHighSpeedInternet: UnitFeaturesFieldMetadata.hasHighSpeedInternet,
  hasSatelliteTv: UnitFeaturesFieldMetadata.hasSatelliteTv,
};

// Typed exports for other unit features
export const OTHER_UNIT_KEYS = ['hasFireplace', 'isFurnished', 'hasPatioBalcony'] as const;
export type OtherUnitKey = (typeof OTHER_UNIT_KEYS)[number];
export const OTHER_UNIT_METADATA: Record<OtherUnitKey, FieldMetadata> = {
  hasFireplace: UnitFeaturesFieldMetadata.hasFireplace,
  isFurnished: UnitFeaturesFieldMetadata.isFurnished,
  hasPatioBalcony: UnitFeaturesFieldMetadata.hasPatioBalcony,
};

// Typed exports for views
export const VIEW_KEYS = [
  'hasCity',
  'hasGolfCourse',
  'hasMountain',
  'hasPark',
  'hasTerritorial',
  'hasWater',
  'hasWooded',
] as const;
export type ViewKey = (typeof VIEW_KEYS)[number];
export const VIEW_METADATA: Record<ViewKey, FieldMetadata> = {
  hasCity: PropertyFeaturesFieldMetadata.hasCity,
  hasGolfCourse: PropertyFeaturesFieldMetadata.hasGolfCourse,
  hasMountain: PropertyFeaturesFieldMetadata.hasMountain,
  hasPark: PropertyFeaturesFieldMetadata.hasPark,
  hasTerritorial: PropertyFeaturesFieldMetadata.hasTerritorial,
  hasWater: PropertyFeaturesFieldMetadata.hasWater,
  hasWooded: PropertyFeaturesFieldMetadata.hasWooded,
};

// Typed exports for security
export const SECURITY_KEYS = [
  'hasControlledAccess',
  'hasDoorman',
  'hasGatedEntry',
  'hasIntercom',
  'hasNightPatrol',
] as const;
export type SecurityKey = (typeof SECURITY_KEYS)[number];
export const SECURITY_METADATA: Record<SecurityKey, FieldMetadata> = {
  hasControlledAccess: PropertyFeaturesFieldMetadata.hasControlledAccess,
  hasDoorman: PropertyFeaturesFieldMetadata.hasDoorman,
  hasGatedEntry: PropertyFeaturesFieldMetadata.hasGatedEntry,
  hasIntercom: PropertyFeaturesFieldMetadata.hasIntercom,
  hasNightPatrol: PropertyFeaturesFieldMetadata.hasNightPatrol,
};

// Typed exports for sport courts
export const SPORT_COURT_KEYS = [
  'hasBasketball',
  'hasBocce',
  'hasMultiPurpose',
  'hasRacquetball',
  'hasShuffleboard',
  'hasSportsField',
  'hasTennis',
  'hasVolleyball',
  'hasOther',
] as const;
export type SportCourtKey = (typeof SPORT_COURT_KEYS)[number];
export const SPORT_COURT_METADATA: Record<SportCourtKey, FieldMetadata> = {
  hasBasketball: PropertyFeaturesFieldMetadata.hasBasketball,
  hasBocce: PropertyFeaturesFieldMetadata.hasBocce,
  hasMultiPurpose: PropertyFeaturesFieldMetadata.hasMultiPurpose,
  hasRacquetball: PropertyFeaturesFieldMetadata.hasRacquetball,
  hasShuffleboard: PropertyFeaturesFieldMetadata.hasShuffleboard,
  hasSportsField: PropertyFeaturesFieldMetadata.hasSportsField,
  hasTennis: PropertyFeaturesFieldMetadata.hasTennis,
  hasVolleyball: PropertyFeaturesFieldMetadata.hasVolleyball,
  hasOther: PropertyFeaturesFieldMetadata.hasOther,
};

// Typed exports for outdoor areas
export const OUTDOOR_AREA_KEYS = [
  'hasDeck',
  'hasGarden',
  'hasGazebo',
  'hasLawn',
  'hasPatio',
  'hasPicnicArea',
  'hasPlayground',
  'hasRooftopDeck',
  'hasTrail',
  'hasWaterFeature',
] as const;
export type OutdoorAreaKey = (typeof OUTDOOR_AREA_KEYS)[number];
export const OUTDOOR_AREA_METADATA: Record<OutdoorAreaKey, FieldMetadata> = {
  hasDeck: PropertyFeaturesFieldMetadata.hasDeck,
  hasGarden: PropertyFeaturesFieldMetadata.hasGarden,
  hasGazebo: PropertyFeaturesFieldMetadata.hasGazebo,
  hasLawn: PropertyFeaturesFieldMetadata.hasLawn,
  hasPatio: PropertyFeaturesFieldMetadata.hasPatio,
  hasPicnicArea: PropertyFeaturesFieldMetadata.hasPicnicArea,
  hasPlayground: PropertyFeaturesFieldMetadata.hasPlayground,
  hasRooftopDeck: PropertyFeaturesFieldMetadata.hasRooftopDeck,
  hasTrail: PropertyFeaturesFieldMetadata.hasTrail,
  hasWaterFeature: PropertyFeaturesFieldMetadata.hasWaterFeature,
};

// Typed exports for property services (otherTypes with category: null)
export const PROPERTY_SERVICE_KEYS = [
  'hasTwentyFourHourMaintenance',
  'hasAssistedLiving',
  'hasBarbecue',
  'hasBicycleStorage',
  'hasCeilingFan',
  'hasDisabledAccess',
  'hasDryCleaningDropOff',
  'hasElevator',
  'hasGuestSuite',
  'hasHotTub',
  'isIncomeRestricted',
  'hasOnlineMaintenancePortal',
  'hasOnlineRentPayment',
  'hasOnsiteManagement',
  'isOver55',
  'hasPackageService',
  'hasPetPark',
  'hasSauna',
  'smokingAllowed',
  'hasSpanishSpeakingStaff',
  'hasStorageSpace',
  'hasSwimmingPool',
  'hasValetTrash',
] as const;
export type PropertyServiceKey = (typeof PROPERTY_SERVICE_KEYS)[number];
export const PROPERTY_SERVICE_METADATA: Record<PropertyServiceKey, FieldMetadata> = {
  hasTwentyFourHourMaintenance: PropertyFeaturesFieldMetadata.hasTwentyFourHourMaintenance,
  hasAssistedLiving: PropertyFeaturesFieldMetadata.hasAssistedLiving,
  hasBarbecue: PropertyFeaturesFieldMetadata.hasBarbecue,
  hasBicycleStorage: PropertyFeaturesFieldMetadata.hasBicycleStorage,
  hasCeilingFan: PropertyFeaturesFieldMetadata.hasCeilingFan,
  hasDisabledAccess: PropertyFeaturesFieldMetadata.hasDisabledAccess,
  hasDryCleaningDropOff: PropertyFeaturesFieldMetadata.hasDryCleaningDropOff,
  hasElevator: PropertyFeaturesFieldMetadata.hasElevator,
  hasGuestSuite: PropertyFeaturesFieldMetadata.hasGuestSuite,
  hasHotTub: PropertyFeaturesFieldMetadata.hasHotTub,
  isIncomeRestricted: PropertyFeaturesFieldMetadata.isIncomeRestricted,
  hasOnlineMaintenancePortal: PropertyFeaturesFieldMetadata.hasOnlineMaintenancePortal,
  hasOnlineRentPayment: PropertyFeaturesFieldMetadata.hasOnlineRentPayment,
  hasOnsiteManagement: PropertyFeaturesFieldMetadata.hasOnsiteManagement,
  isOver55: PropertyFeaturesFieldMetadata.isOver55,
  hasPackageService: PropertyFeaturesFieldMetadata.hasPackageService,
  hasPetPark: PropertyFeaturesFieldMetadata.hasPetPark,
  hasSauna: PropertyFeaturesFieldMetadata.hasSauna,
  smokingAllowed: PropertyFeaturesFieldMetadata.smokingAllowed,
  hasSpanishSpeakingStaff: PropertyFeaturesFieldMetadata.hasSpanishSpeakingStaff,
  hasStorageSpace: PropertyFeaturesFieldMetadata.hasStorageSpace,
  hasSwimmingPool: PropertyFeaturesFieldMetadata.hasSwimmingPool,
  hasValetTrash: PropertyFeaturesFieldMetadata.hasValetTrash,
};

// Typed category-specific exports for utilities
export const UTILITIES_KEYS = [
  'hasAirConditioning',
  'hasCableTv',
  'hasElectricity',
  'hasGarbage',
  'hasGas',
  'hasHeating',
  'hasHotWater',
  'hasInternet',
  'hasSewage',
  'hasWater',
] as const;
export type UtilityKey = (typeof UTILITIES_KEYS)[number];
export const UTILITIES_METADATA: Record<UtilityKey, FieldMetadata> = {
  hasAirConditioning: TermFeaturesFieldMetadata.hasAirConditioning,
  hasCableTv: TermFeaturesFieldMetadata.hasCableTv,
  hasElectricity: TermFeaturesFieldMetadata.hasElectricity,
  hasGarbage: TermFeaturesFieldMetadata.hasGarbage,
  hasGas: TermFeaturesFieldMetadata.hasGas,
  hasHeating: TermFeaturesFieldMetadata.hasHeating,
  hasHotWater: TermFeaturesFieldMetadata.hasHotWater,
  hasInternet: TermFeaturesFieldMetadata.hasInternet,
  hasSewage: TermFeaturesFieldMetadata.hasSewage,
  hasWater: TermFeaturesFieldMetadata.hasWater,
};

// Typed category-specific exports for lease lengths
export const LEASE_LENGTH_TYPE_KEYS = [
  'hasFlexible',
  'hasMonthly',
  'hasSixMonths',
  'hasOneYear',
  'hasLessThanOneYear',
  'hasMoreThanOneYear',
  'hasNone',
] as const;
export type LeaseLengthKey = (typeof LEASE_LENGTH_TYPE_KEYS)[number];
export const LEASE_LENGTH_METADATA: Record<LeaseLengthKey, FieldMetadata> = {
  hasFlexible: TermFeaturesFieldMetadata.hasFlexible,
  hasMonthly: TermFeaturesFieldMetadata.hasMonthly,
  hasSixMonths: TermFeaturesFieldMetadata.hasSixMonths,
  hasOneYear: TermFeaturesFieldMetadata.hasOneYear,
  hasLessThanOneYear: TermFeaturesFieldMetadata.hasLessThanOneYear,
  hasMoreThanOneYear: TermFeaturesFieldMetadata.hasMoreThanOneYear,
  hasNone: TermFeaturesFieldMetadata.hasNone,
};

// Typed category-specific exports for parking types
export const PARKING_TYPE_TYPE_KEYS = [
  'hasAttachedGarage',
  'hasCoveredParking',
  'hasDetachedGarage',
  'hasParkingLot',
  'hasMultiLevelGarage',
  'hasUndergroundGarage',
  'hasNoneParking',
] as const;
export type ParkingTypeKey = (typeof PARKING_TYPE_TYPE_KEYS)[number];
export const PARKING_TYPE_METADATA: Record<ParkingTypeKey, FieldMetadata> = {
  hasAttachedGarage: TermFeaturesFieldMetadata.hasAttachedGarage,
  hasCoveredParking: TermFeaturesFieldMetadata.hasCoveredParking,
  hasDetachedGarage: TermFeaturesFieldMetadata.hasDetachedGarage,
  hasParkingLot: TermFeaturesFieldMetadata.hasParkingLot,
  hasMultiLevelGarage: TermFeaturesFieldMetadata.hasMultiLevelGarage,
  hasUndergroundGarage: TermFeaturesFieldMetadata.hasUndergroundGarage,
  hasNoneParking: TermFeaturesFieldMetadata.hasNoneParking,
};

export {
  KeyFeaturesConstants,
  KeyFeaturesCategoryKeys,
  KeyFeaturesFieldMetadata,
  PropertyFeaturesConstants,
  PropertyFeaturesCategoryKeys,
  PropertyFeaturesFieldMetadata,
  UnitFeaturesConstants,
  UnitFeaturesCategoryKeys,
  UnitFeaturesFieldMetadata,
  TermFeaturesConstants,
  TermFeaturesCategoryKeys,
  TermFeaturesFieldMetadata,
};

/** Where an amenity lives in `/details`: `buildingAttributes[key]`, or `buildingAttributes[category][key]`. */
export type AmenityLocation = { key: string; category: string | null };

/** Indexed by the name the wire uses: the apiKey, or the feedFieldName on the feed override endpoint. */
export const AMENITY_LOCATION_BY_FIELD: Record<string, AmenityLocation> = Object.fromEntries(
  [
    KeyFeaturesFieldMetadata,
    PropertyFeaturesFieldMetadata,
    UnitFeaturesFieldMetadata,
    TermFeaturesFieldMetadata,
  ].flatMap((metadata) =>
    Object.entries(metadata as Record<string, FieldMetadata>).flatMap(([key, { apiKey, feedFieldName, category }]) =>
      [apiKey, feedFieldName].filter(Boolean).map((name) => [name, { key, category }]),
    ),
  ),
);

export const LAUNDRY_FIELD_NAME_BY_TYPE: Record<RentalManagerLaundryType, string> = {
  [RentalManagerLaundryType.HOOKUPS]: 'hasHookupsLaundry',
  [RentalManagerLaundryType.IN_UNIT]: 'hasInUnitLaundry',
  [RentalManagerLaundryType.NONE]: 'hasNoneLaundry',
  [RentalManagerLaundryType.SHARED]: 'hasSharedLaundry',
};

/** The `/details` `laundry` value, which is the RADIO_OPTIONS key, for each laundry flag. */
export const LAUNDRY_VALUE_BY_FIELD: Record<string, string> = Object.fromEntries(
  Object.entries(KeyFeaturesConstants.RADIO_OPTIONS ?? {}).map(([value, { key }]) => [
    LAUNDRY_FIELD_NAME_BY_TYPE[key],
    value,
  ]),
);
